import React, { useCallback, useEffect, useRef, useState } from 'react';
import OpportunityEvents from 'App/babel/resources/OpportunityEvents';
import HistoryLog from '../HistoryLog';
import Opportunity from 'App/resources/Model/Opportunity';
import usePartialLoader, { FetcherFunction } from 'App/components/hooks/usePartialLoader';
import { getTimelineRow } from 'Components/Helpers/getTimelineRow';
import T from 'Components/Helpers/translate';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore';
import type { HistoryLogRenderedBy } from 'App/components/HistoryLog/ModalHistoryLog';
import PriorHistoryLog from '../PriorHistoryLog/PriorHistoryLog';

import './OpportunityHistoryLog.scss';
import useSelector from 'App/components/hooks/useSelector/useSelector';
import Event from 'App/resources/Model/Event';
import HistoryLogSearch from '../HistoryLogSearch';
import { expandHistoryLogSearch } from '../historyLogSearchSynonyms';
import EventBus from 'App/services/EventBus';
import type Comment from 'App/resources/Model/Comment';
import type { BasicUser } from 'App/resources/Model/User';

type OpportunityHistoryLogProps = {
	opportunity: Opportunity;
	selectButtons?: React.ReactElement;
	showLeading?: boolean;
	renderedBy: HistoryLogRenderedBy;
};

// No comment channels here: the broadcast has no entity filter, so listening for one would
// re-fetch the whole events list on every comment added anywhere in the CRM. Comments hanging off
// an event card are fetched by that card (TimelineGeneric, which filters by entity id); a comment
// written on the deal itself is handled below.
const listeners = [
	'activity.deleted',
	'appointment.added',
	'appointment.deleted',
	'appointment.updated',
	'mail.updated',
	'activity.updated'
];

// A comment written on the deal does get an event row of its own, so the list is one row short
// until something else makes it refetch - which is why the comment only turned up after leaving
// edit mode (WEB-10577). Refetching on the broadcast is not the answer: the route answers only
// with events dated strictly *before* now (base/src/core/services/opportunityEvents.ts
// getTimelineForOpportunityId), so a refetch a moment later would still come back without the
// comment. The row is built from the broadcast payload instead, and drops out again as soon as
// the fetched list carries it.
const commentEvent = (comment: Comment): Event => ({
	entityId: comment.id,
	entityType: 'Comment',
	date: comment.regDate,
	// A comment's `user` relation is id and name only (base/src/domain/repositories/relations/comment.js),
	// where the events route hands back the whole user. The card reads the name off it and nothing else.
	users: comment.user ? [comment.user as BasicUser] : [],
	comment
});

const fetcher: FetcherFunction<{ opportunity: Opportunity; search?: string }, Event> = (
	filters,
	{ opportunity, search }
) => {
	if (!opportunity?.id) {
		return Promise.resolve({ data: [], metadata: { total: 0 } });
	}
	return OpportunityEvents.getEvents(
		opportunity.id,
		filters.limit,
		filters.offset,
		false,
		search ? expandHistoryLogSearch(search) : undefined
	);
};

const OpportunityHistoryLog: React.FC<OpportunityHistoryLogProps> = ({
	opportunity,
	selectButtons,
	showLeading = false,
	renderedBy
}) => {
	const notesFilterActive = useSelector(state => state.NotesFilterActive);
	const [search, setSearch] = useState('');
	const onSearchChange = useCallback((value: string) => setSearch(value), []);

	const {
		data: events,
		total: totalEvents,
		loading,
		loadMore,
		reset
	} = usePartialLoader({
		fetcher,
		broadcastTypes: listeners,
		fetcherProps: {
			opportunity,
			search
		}
	});

	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		reset();
	}, [search]);

	// `comment.added` reaches every session at the customer, for every comment in the CRM, so only
	// the ones written on this deal are kept.
	const [postedComments, setPostedComments] = useState<Comment[]>([]);
	useEffect(
		() =>
			EventBus.on('comment.added', (_event, comment: Comment) => {
				if (opportunity?.id && comment.opportunity?.id === opportunity.id) {
					setPostedComments(previous => [comment, ...previous]);
				}
			}),
		[opportunity?.id]
	);

	// Latch search visibility so clearing the query doesn't unmount the input
	// (which would steal focus) while the timeline refetches.
	const hasShownSearchRef = useRef(false);
	if (totalEvents > 0 || search) {
		hasShownSearchRef.current = true;
	}
	const showSearchInput = hasShownSearchRef.current;

	if (!opportunity?.id) {
		return null;
	}

	const showLeadingUpTo = showLeading && !!opportunity.source?.id;

	// Dropped once the fetched list carries the comment, and left out entirely while searching -
	// the search runs on the server and a locally held row has not been through it.
	const fetchedCommentIds = new Set(
		events.filter(event => event.entityType === 'Comment').map(event => event.entityId)
	);
	const pendingComments = search ? [] : postedComments.filter(comment => !fetchedCommentIds.has(comment.id));

	return (
		<HistoryLog
			title={T('history')}
			selectButtons={selectButtons}
			searchInput={
				showSearchInput ? <HistoryLogSearch onSearchChange={onSearchChange} color="grey-2" /> : null
			}
			hasPriorHistory={showLeadingUpTo}
			loading={loading}
		>
			{pendingComments.map(comment => (
				<div key={`comment-${comment.id}`}>
					{getTimelineRow(commentEvent(comment), 'Opportunity', notesFilterActive, false, renderedBy)}
				</div>
			))}
			{events.map((event, index) => {
				return (
					<div key={index}>
						{getTimelineRow(
							event,
							'Opportunity',
							opportunity.id === event.entityId ? false : notesFilterActive,
							events.length - 1 === index,
							renderedBy
						)}
					</div>
				);
			})}
			{events && events.length < totalEvents ? (
				<TimelineLoadMore
					newEventsLoading={loading}
					numEventsLoaded={events.length}
					totalEvents={totalEvents}
					incrementOffset={loadMore}
				/>
			) : null}
			{showLeadingUpTo ? <PriorHistoryLog type={'opportunity'} entity={opportunity} /> : null}
		</HistoryLog>
	);
};

export default OpportunityHistoryLog;
